import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { PRODUCT_CATEGORIES, PRODUCTS, getProductsByCategory, getCategoryById } from '../data/products';
import { ProductCard } from '../components/common/ProductCard';
import Seo from '../components/common/Seo';
import { getRouteMeta } from '../routeMeta';

const ProductsPage = () => {
  const { category } = useParams();
  const [activeCategory, setActiveCategory] = useState(category || 'all');
  const categoryImageFitClass = (fit) => (fit === 'contain' ? 'object-contain p-5' : 'object-cover');

  useEffect(() => {
    if (category) {
      setActiveCategory(category);
    }
  }, [category]);

  const filteredProducts = activeCategory === 'all' ? PRODUCTS : getProductsByCategory(activeCategory);
  const currentCategory = activeCategory !== 'all' ? getCategoryById(activeCategory) : null;
  const seoPath = category ? `/products/${category}` : '/products';

  return (
    <div data-testid="products-page">
      <Seo {...getRouteMeta(seoPath)} path={seoPath} />
      <section className="page-hero-shell tech-grid relative overflow-hidden py-16 md:py-24">
        <div className="tech-orb tech-orb--pulse absolute -left-16 top-8 h-40 w-40 bg-[#2563EB]/25" />
        <div className="tech-orb absolute right-12 top-10 h-56 w-56 bg-cyan-400/10" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 text-center">
            <div className="mb-6 flex items-center justify-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-white">Home</Link>
              <span>/</span>
              <span className="text-white">Products</span>
              {currentCategory && (
                <>
                  <span>/</span>
                  <span className="text-[#60A5FA]">{currentCategory.name}</span>
                </>
              )}
            </div>

            <h1 className="font-['Manrope'] text-4xl font-extrabold uppercase tracking-tight text-white md:text-6xl">
              {currentCategory ? currentCategory.name : 'Our Products'}
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-xl leading-relaxed text-slate-300">
              {currentCategory
                ? `${currentCategory.description} Built for industrial weighing requirements across Ghana, India, Africa, Dubai, and international markets.`
                : 'Complete range of weighbridges, truck scales, platform scales, indicators, and weighing software designed for precision, reliability, and durability across Ghana, India, Africa, Dubai, and international markets.'}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="tech-grid bg-slate-950/95 py-18 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="mb-6 flex items-center gap-2">
              <Filter className="h-5 w-5 text-[#60A5FA]" />
              <span className="text-sm font-bold uppercase tracking-wider text-white">Filter by Category</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setActiveCategory('all')}
                className={`rounded-sm px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
                  activeCategory === 'all'
                    ? 'bg-[#1E40AF] text-white shadow-[0_18px_40px_rgba(30,64,175,0.35)]'
                    : 'border border-white/10 bg-white/5 text-slate-200 hover:border-[#60A5FA] hover:bg-white/10'
                }`}
                data-testid="filter-all"
              >
                All Products
              </button>
              {PRODUCT_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`rounded-sm px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-all ${
                    activeCategory === cat.id
                      ? 'bg-[#1E40AF] text-white shadow-[0_18px_40px_rgba(30,64,175,0.35)]'
                      : 'border border-white/10 bg-white/5 text-slate-200 hover:border-[#60A5FA] hover:bg-white/10'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-8">
            <p className="text-slate-400">
              Showing <span className="font-bold text-[#60A5FA]">{filteredProducts.length}</span> products
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filteredProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg text-slate-400">No products found in this category.</p>
              <Button onClick={() => setActiveCategory('all')} className="tech-shimmer mt-4 bg-[#1E40AF] text-white hover:bg-[#2563EB]">
                View All Products
              </Button>
            </div>
          )}
        </div>
      </section>

      {activeCategory === 'all' && (
        <section className="tech-grid bg-[#08152b] py-18 md:py-28">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16 text-center">
              <h2 className="font-['Manrope'] text-3xl font-bold uppercase tracking-tight text-white md:text-4xl">
                Browse by Category
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {PRODUCT_CATEGORIES.map((cat, index) => (
                <motion.div key={cat.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  <Link
                    to={`/products/${cat.id}`}
                    className="tech-panel depth-card group flex gap-6 border border-white/10 bg-white/5 p-6 transition-all hover:border-[#60A5FA]"
                    data-testid={`category-card-${cat.id}`}
                  >
                    <div className="h-32 w-32 flex-shrink-0 overflow-hidden border border-white/10 bg-[radial-gradient(circle_at_top,rgba(96,165,250,0.14),transparent_42%),linear-gradient(180deg,#112448_0%,#0B1730_100%)]">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        loading="lazy"
                        className={`media-grade h-full w-full transition-transform duration-500 group-hover:scale-105 ${categoryImageFitClass(cat.imageFit)}`}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold leading-tight text-white transition-colors group-hover:text-[#60A5FA]">
                        {cat.name}
                      </h3>
                      <p className="mt-3 text-base leading-7 text-slate-300">
                        {cat.description}
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-[#60A5FA]">
                        <span>View Products</span>
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="tech-grid relative overflow-hidden bg-[#08152b] py-16">
        <div className="tech-orb absolute left-16 top-1/2 h-48 w-48 -translate-y-1/2 bg-[#1E40AF]/20" />
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8">
          <h3 className="mb-6 text-2xl font-bold text-white md:text-3xl">
            Need help choosing the right product?
          </h3>
          <p className="mx-auto mb-8 max-w-xl text-white/80">
            Our engineering team can help you select the right weighbridge, truck scale, or weighing system for your site requirements in Ghana, India, Africa, Dubai, or other international markets.
          </p>
          <Link to="/contact">
            <Button className="tech-shimmer rounded-sm bg-[#1E40AF] px-8 py-4 font-bold uppercase tracking-wider text-white shadow-[0_20px_50px_rgba(30,64,175,0.35)] hover:bg-[#2563EB]" data-testid="products-cta-btn">
              Contact Our Experts
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
